import type { RegisterDiscountInfo } from "@/types/auth";

// After a successful registration that earned a qr_discount_code, the user
// is redirected home with the discount encoded on the URL (see
// buildHomeDiscountUrl) so the homepage — not /register — is what shows the
// celebration modal, per product requirement: the signup flow must actually
// finish (redirect home) before anything else happens.
const QUERY_KEY = "discount";

// Builds the post-register redirect URL. Returns null when there's no
// usable code to show (missing/blank/wrong type despite what the type
// declares) — the caller should redirect to a plain "/" in that case rather
// than send the user to a URL that would render a broken discount modal.
export function buildHomeDiscountUrl(discount: RegisterDiscountInfo | null | undefined): string | null {
  if (!discount) return null;

  const code = typeof discount.code === "string" ? discount.code.trim() : "";
  if (!code) return null;

  const params = new URLSearchParams({ [QUERY_KEY]: code });

  if (typeof discount.discount_value === "number" && Number.isFinite(discount.discount_value)) {
    params.set("discount_value", String(discount.discount_value));
  }

  if (typeof discount.expires_at === "string" && discount.expires_at.trim()) {
    params.set("discount_expires", discount.expires_at);
  }

  return `/?${params.toString()}`;
}

export function readDiscountFromParams(
  searchParams: URLSearchParams | { get(key: string): string | null },
): RegisterDiscountInfo | null {
  const code = searchParams.get(QUERY_KEY);
  if (!code) return null;

  const rawValue = searchParams.get("discount_value");
  const parsedValue = rawValue ? Number(rawValue) : 0;
  const discount_value = Number.isFinite(parsedValue) ? parsedValue : 0;

  // Absent, empty, or otherwise unusable expiry just means the modal skips
  // that line (see formatExpiryDate in DiscountCodeModal) — never a reason
  // to fail this whole read.
  const expires_at = searchParams.get("discount_expires") || undefined;

  return { code, discount_value, expires_at };
}
